import {getApp} from "firebase/app";
import {doc, getDoc} from "firebase/firestore";
import {getFunctions, httpsCallable} from "firebase/functions";
import {auth, db} from "../../lib/firebase/config";
import {emitCoinDrop} from "./coinDropEmitter";

export const TRANSACTION_REASONS = {
  BOOK_COMPLETED: "BOOK_COMPLETED",
  POMODORO_SESSION: "POMODORO_SESSION",
  THEME_PURCHASE: "THEME_PURCHASE",
  SOCIAL_SHARE_BONUS: "SOCIAL_SHARE_BONUS",
} as const;

export type TransactionReasonCode = (typeof TRANSACTION_REASONS)[keyof typeof TRANSACTION_REASONS];
export type WalletTransactionType = "credit" | "debit";

interface WalletState {
  coins: number;
}

interface UserStatsEconomyDoc {
  wallet?: WalletState;
}

export interface WalletTransaction {
  type: WalletTransactionType;
  amount: number;
  reasonCode: TransactionReasonCode;
  idempotencyKey: string;
  balanceAfter: number;
  createdAt: unknown;
}

export interface EconomyOperationResult {
  applied: boolean;
  balance: number;
  transactionId: string | null;
}

export class InsufficientFundsError extends Error {
  constructor(message = "Saldo insuficiente") {
    super(message);
    this.name = "InsufficientFundsError";
  }
}

function normalizeCoins(raw: unknown): number {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
}

function assertValidAmount(amount: number): void {
  if (
    !Number.isFinite(amount) ||
    amount <= 0 ||
    !Number.isInteger(amount)
  ) {
    throw new Error("amount must be a positive integer");
  }
}

const functionsRegion = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as
  | string
  | undefined;

function getFunctionsInstance() {
  const app = getApp();
  return functionsRegion && functionsRegion.trim().length > 0
    ? getFunctions(app, functionsRegion.trim())
    : getFunctions(app);
}

function assertValidIdempotencyKey(idempotencyKey: string): void {
  if (typeof idempotencyKey !== "string" || idempotencyKey.trim().length === 0) {
    throw new Error("idempotencyKey is required");
  }
}

function getStatsRef(userId: string) {
  return doc(db, "user_stats", userId);
}

async function getCurrentBalanceOrZero(userId: string): Promise<number> {
  const snap = await getDoc(getStatsRef(userId));
  if (!snap.exists()) return 0;
  const data = snap.data() as UserStatsEconomyDoc;
  return normalizeCoins(data.wallet?.coins);
}

function isCallableFailedPrecondition(e: unknown): boolean {
  if (typeof e !== "object" || e === null) return false;
  const code = (e as {code?: string}).code;
  return code === "functions/failed-precondition";
}

export async function awardCoins(
  userId: string,
  amount: number,
  reasonCode: TransactionReasonCode,
  idempotencyKey: string,
  options?: {originId?: string},
): Promise<EconomyOperationResult> {
  assertValidAmount(amount);
  assertValidIdempotencyKey(idempotencyKey);
  if (!auth.currentUser || auth.currentUser.uid !== userId) {
    throw new Error("User must be signed in to award coins");
  }
  const award = httpsCallable<
    {amount: number; reasonCode: TransactionReasonCode; idempotencyKey: string},
    EconomyOperationResult
  >(getFunctionsInstance(), "awardCoins");
  const res = await award({amount, reasonCode, idempotencyKey});
  const data = res.data;
  if (data == null) {
    throw new Error("awardCoins: empty response");
  }
  if (data.applied && amount > 0) {
    emitCoinDrop({
      amount,
      balanceAfter: data.balance,
      reasonCode,
      originId: options?.originId,
    });
  }
  return data;
}

/**
 * Débito via Cloud Function. Em saldo insuficiente lança {@link InsufficientFundsError}
 * (código `functions/failed-precondition` no backend) para o caller exibir toast.
 */
export async function spendCoins(
  userId: string,
  amount: number,
  reasonCode: TransactionReasonCode,
  idempotencyKey: string,
  /** Obrigatório em THEME_PURCHASE — o servidor valida o preço e entrega o unlock na mesma transação. */
  themeId?: string,
): Promise<EconomyOperationResult> {
  assertValidAmount(amount);
  assertValidIdempotencyKey(idempotencyKey);
  if (!auth.currentUser || auth.currentUser.uid !== userId) {
    throw new Error("User must be signed in to spend coins");
  }
  const trimmedKey = idempotencyKey.trim();
  const spend = httpsCallable<
    {amount: number; reasonCode: TransactionReasonCode; idempotencyKey: string; themeId?: string},
    EconomyOperationResult
  >(getFunctionsInstance(), "spendCoins");
  try {
    const res = await spend({
      amount,
      reasonCode,
      idempotencyKey: trimmedKey,
      ...(themeId ? {themeId} : {}),
    });
    const data = res.data;
    if (data == null) {
      throw new Error("spendCoins: empty response");
    }
    return data;
  } catch (e: unknown) {
    if (isCallableFailedPrecondition(e)) {
      throw new InsufficientFundsError("Saldo insuficiente");
    }
    throw e;
  }
}

export async function getWalletBalance(userId: string): Promise<number> {
  return getCurrentBalanceOrZero(userId);
}
